import React, { useState } from "react";
import { toast } from "sonner";
import { Globe, Smartphone, Copy, Check, Send, RefreshCw, ArrowRight, Loader2, Download } from "lucide-react";
import { MERIDIAN_TRACK_WRITE_KEY, SDK_API_HOST, SDK_SCRIPT_URL, buildInstallSnippet, fireTrackTestEvent } from "../../../components/hook/use-track-collect";

// Wired to the real backend per the backend team's write-up (2026-09-23,
// confirmed live 2026-09-24):
// - Install snippet: the per-tenant snippet endpoint only ever accepts one
//   admin key — never the login token, never a tenant header, because
//   there's no tenant_id concept in this system at all (the write key IS
//   the identifier). It can't work from this app's session, so it's never
//   called; backend confirmed there's nothing behind it beyond this exact
//   static text with the write key filled in, so it's built client-side.
// - "Download SDK": there's no separate downloadable SDK *file* — the
//   integration is this snippet loading the real hosted script by URL.
//   The button instead downloads a small JSON config (script url, api
//   host, write key, ready-to-paste snippet) so there's still something
//   concrete to save, without navigating to the script URL directly (that
//   only ever produced a blank/404 tab — it's meant to be referenced by
//   <script src>, not opened).
// - "Send test event": POST /collect/v1/events with the real write key —
//   previously just flipped local state with no request at all.
// Mobile has no confirmed snippet from backend yet — its install/init
// examples stay clearly-flagged placeholders.

type Platform = "website" | "mobile";

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy the snippet manually.");
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 mt-3 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
      <code className="text-xs font-mono text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-nowrap">{code}</code>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        title="Copy"
      >
        {copied ? <Check size={15} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={15} />}
      </button>
    </div>
  );
};

const StepMarker: React.FC<{ done: boolean; number: number }> = ({ done, number }) => (
  <span
    className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0 ${
      done ? "bg-emerald-500 text-white" : "border border-gray-300 dark:border-gray-600 text-[#7B7B7B] font-semibold dark:text-gray-500"
    }`}
  >
    {done ? <Check size={13} /> : number}
  </span>
);

const TrackSetupGuide: React.FC = () => {
  const [eventSent, setEventSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastAnonymousId, setLastAnonymousId] = useState("");
  const [platform, setPlatform] = useState<Platform>("website");

  const writeKey = MERIDIAN_TRACK_WRITE_KEY;
  const scriptSnippet = buildInstallSnippet(writeKey);
  // Confirmed global is `window.dalmSDK` (matches the .load(...) call above)
  // — .track(...) itself isn't spelled out by backend yet, but follows the
  // same object's naming rather than the old, disproven "dalm"/"Dalm" guess.
  const trackSnippet = "window.dalmSDK.track('Signup Completed')";
  const mobileInstallSnippet = `npm install @dalm/react-native-sdk`;
  const mobileInitSnippet = `Dalm.init("${writeKey}")`;

  const handleDownloadSdk = () => {
    const config = {
      write_key: writeKey,
      sdk_script_url: SDK_SCRIPT_URL,
      api_host: SDK_API_HOST,
      install_snippet: scriptSnippet,
    };
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dalm-sdk-config.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Matches the reference's own (slightly quirky) counter — steps 1 & 2 are
  // informational and always read as done; sending the test event jumps
  // straight to "4 of 4" even though step 4 itself stays a manual step.
  const completedCount = eventSent ? 4 : 2;
  const progressPct = (completedCount / 4) * 100;

  const handleSendTestEvent = async () => {
    setSending(true);
    const anonymousId = `anon_${Math.random().toString(36).slice(2, 10)}`;
    const result = await fireTrackTestEvent(writeKey, "Signup Completed", anonymousId);
    setSending(false);
    if (result.ok) {
      setEventSent(true);
      setLastAnonymousId(anonymousId);
      toast.success("Test event sent.");
    } else {
      toast.error(result.detail || "Failed to send test event.");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 p-4 sm:p-6">
      <div className="max-w-3xl space-y-6">
        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">Setup guide</h1>
              <span
                className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500"
                title="Every org sees this same key today — per-tenant keys need a backend change (the collector accepting the login JWT) that hasn't shipped yet."
              >
                Write key {writeKey} · Demo tenant
              </span>
            </div>
            <span className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">
              Setup: {completedCount} of 4 steps
            </span>
          </div>
          <div className="h-1 rounded-full bg-gray-100 dark:bg-gray-800 mt-3 overflow-hidden">
            <div className="h-full bg-primary-600 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Install method */}
        <div className="flex flex-wrap items-center gap-2">
          {(
            [
              { id: "website", label: "Website", icon: <Globe size={15} /> },
              { id: "mobile", label: "Mobile", icon: <Smartphone size={15} /> },
            ] as { id: Platform; label: string; icon: React.ReactNode }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setPlatform(t.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold transition-colors ${
                platform === t.id
                  ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900"
                  : "border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="space-y-6">
          <div className="flex gap-3.5">
            <StepMarker done number={1} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Install</p>
              <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
                {platform === "website"
                  ? "Add this snippet inside your site's <head> tag — download it as a config file for reference, or paste it directly."
                  : "Add the SDK to your app, then initialise it with your write key."}
              </p>
              {platform === "website" ? (
                <>
                  <button
                    type="button"
                    onClick={handleDownloadSdk}
                    className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    <Download size={14} /> Download SDK config
                  </button>
                  <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-3">Or paste this script tag directly:</p>
                  <CodeBlock code={scriptSnippet} />
                </>
              ) : (
                <>
                  <CodeBlock code={mobileInstallSnippet} />
                  <CodeBlock code={mobileInitSnippet} />
                </>
              )}
            </div>
          </div>

          <div className="flex gap-3.5">
            <StepMarker done number={2} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Track your first event</p>
              <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
                {platform === "website" ? "The SDK auto-fires Page Viewed." : "The SDK auto-fires Application Opened."} Add a custom event anywhere:
              </p>
              <CodeBlock code={trackSnippet} />
            </div>
          </div>

          <div className="flex gap-3.5">
            <StepMarker done={eventSent} number={3} />
            <div className="min-w-0 flex-1">
              <div
                className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg ${
                  eventSent ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-amber-50 dark:bg-amber-500/10"
                }`}
              >
                <span
                  className={`flex items-center gap-2 text-sm font-medium ${
                    eventSent ? "text-emerald-800 dark:text-emerald-300" : "text-amber-800 dark:text-amber-300"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${eventSent ? "bg-emerald-500" : "bg-amber-500"}`} />
                  {eventSent ? "Event received — just now" : "Waiting for your first event…"}
                </span>
                <button
                  type="button"
                  onClick={handleSendTestEvent}
                  disabled={sending}
                  className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? <Loader2 size={13} className="animate-spin" /> : eventSent ? <RefreshCw size={13} /> : <Send size={13} />}
                  {sending ? "Sending…" : eventSent ? "Send another" : "Send test event"}
                </button>
              </div>
              {eventSent && (
                <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-2">
                  Signup Completed · {writeKey} · {lastAnonymousId} · 202 Accepted
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3.5">
            <StepMarker done={false} number={4} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Identify logged-in users</p>
              <p className="text-[12px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
                Merges anonymous browsing history into a known profile once someone logs in.
              </p>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-900">
          {[
            ["View in Live Stream", "/track/live-event-stream"],
            ["Canonical event names", "/track/event-schema"],
            ["Troubleshooting", "/track/setup"],
          ].map(([label, href]) => (
            <a key={label} href={href} className="flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
              {label} <ArrowRight size={13} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrackSetupGuide;
